/**
 * ecclesialDiscernmentCoordinator.js
 *
 * Master coordinator for the Ecclesial Relational Intelligence layer.
 * Produces ONE compact [DISCERNIMIENTO ECLESIAL] system prompt block.
 *
 * Answers: "What systemic or relational dynamics in the church community
 * should shape how Kevia responds right now?"
 *
 * DISTINCT FROM prior layers:
 *   [relationship:]  — per-entity history
 *   [trajectory:]    — entity trajectory snapshot
 *   [CONTINUIDAD]    — longitudinal spiritual arc
 *   [JUICIO PASTORAL]— complexity/principle/process
 *   [FORMACIÓN]      — spiritual identity/posture
 *   [DISCERNIMIENTO] — timing/depth/question/empathy
 * THIS LAYER ADDS:
 *   Systemic relational context — family dynamics, congregational health,
 *   load distribution, conflict signals, care network, silent tensions.
 *
 * Signal gate: omitted when NO ecclesial signals are active.
 * Max block: 600 chars.
 *
 * Design:
 *  — Pure function; no async, no side effects
 *  — Calls getDistrictState() for ambient congregational data
 *  — Consumes assessment.emotion + assessment.ethics (no duplicate detection)
 */

import { inferRelationalContext }        from "./relationalContextEngine";
import { detectFamilyDynamics }          from "./familySystemDiscernment";
import { detectCongregationalSignals }   from "./congregationalHealthSignals";
import { assessLoadDistribution }        from "./pastoralLoadDistribution";
import { detectConflictSignals }         from "./conflictSensitivityEngine";
import { inferCareNetwork }              from "./influenceAndCareNetwork";
import { detectSilentTension }           from "./silentTensionDetector";
import { getDistrictState }              from "../kevia-data/districtStateEngine";

const MAX_ECCLESIAL_CHARS = 600;

/**
 * @param {{
 *   text:               string,
 *   intent:             string,
 *   emotion:            object|null,          — from assessment.emotion
 *   riskLevel:          string,
 *   ethics:             object,               — from assessment.ethics
 *   entityContext:      object|null,
 *   conversationLength: number,
 *   continuityPattern:  string,               — from FASE 9 continuity.pattern
 *   judgmentComplexity: string,               — from FASE 8 judgment.complexity
 * }} params
 * @returns {{
 *   ecclesialContext:    string|null,
 *   relationalStructure: string,
 *   familyDynamic:       string,
 *   congregationSignal:  string,
 *   loadState:           string,
 *   conflictType:        string,
 *   carePattern:         string,
 *   silentTension:       number,
 *   isActive:            boolean,
 * }}
 */
export function buildEcclesialContext({
  text               = "",
  intent             = "general",
  emotion            = null,
  riskLevel          = "none",
  ethics             = {},
  entityContext      = null,
  conversationLength = 0,
  continuityPattern  = "sin_patron",
  judgmentComplexity = "simple",
}) {
  const nullResult = {
    ecclesialContext:    null,
    relationalStructure: "general",
    familyDynamic:       "ninguno",
    congregationSignal:  "ninguno",
    loadState:           "low",
    conflictType:        "ninguno",
    carePattern:         "ninguno",
    silentTension:       0,
    isActive:            false,
  };

  // ── Run all 7 subordinate modules ────────────────────────────────────────
  const relationalResult    = inferRelationalContext({ text, intent, entityContext });
  const familyResult        = detectFamilyDynamics({ text, emotion, intent });
  const districtState       = getDistrictState();
  const congregationResult  = detectCongregationalSignals({ text, emotion, intent, districtState });
  const loadResult          = assessLoadDistribution({ text, intent, emotion });
  const conflictResult      = detectConflictSignals({ text, emotion, intent, ethics });
  const careResult          = inferCareNetwork({ text, emotion, intent, entityContext });
  const silentResult        = detectSilentTension({ text, emotion, intent });

  // ── Signal gate ───────────────────────────────────────────────────────────
  // Only activate if at least one non-trivial signal is present
  const hasSignals =
    familyResult.dynamic       !== "ninguno" ||
    congregationResult.signal  !== "ninguno" ||
    loadResult.isOverCentered                ||
    conflictResult.type        !== "ninguno" ||
    careResult.hasImbalance                  ||
    silentResult.hasTension;

  if (!hasSignals) {
    return {
      ...nullResult,
      relationalStructure: relationalResult.structure,
    };
  }

  // ── Collect non-null instruction lines (priority order) ───────────────────
  const lines = [];

  // 1. Silent tension first — shapes everything else (listen before speak)
  if (silentResult.instruction)      lines.push(silentResult.instruction);

  // 2. Conflict sensitivity
  if (conflictResult.instruction)    lines.push(conflictResult.instruction);

  // 3. Family dynamics
  if (familyResult.instruction)      lines.push(familyResult.instruction);

  // 4. Congregational health
  if (congregationResult.instruction) lines.push(congregationResult.instruction);

  // 5. Load distribution
  if (loadResult.instruction)        lines.push(loadResult.instruction);

  // 6. Care network imbalance
  if (careResult.instruction)        lines.push(careResult.instruction);

  if (lines.length === 0) {
    return {
      ...nullResult,
      relationalStructure: relationalResult.structure,
      familyDynamic:       familyResult.dynamic,
      congregationSignal:  congregationResult.signal,
      loadState:           loadResult.intensity,
      conflictType:        conflictResult.type,
      carePattern:         careResult.pattern,
      silentTension:       silentResult.count,
    };
  }

  const block = lines.join("\n").slice(0, MAX_ECCLESIAL_CHARS);

  return {
    ecclesialContext:    block,
    relationalStructure: relationalResult.structure,
    familyDynamic:       familyResult.dynamic,
    congregationSignal:  congregationResult.signal,
    loadState:           loadResult.intensity,
    conflictType:        conflictResult.type,
    carePattern:         careResult.pattern,
    silentTension:       silentResult.count,
    isActive:            true,
  };
}
